import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { Sidebar } from 'primereact/sidebar'
import {
  UrgencyDailyPointDTO,
  UrgencyDimensionRowDTO,
  UrgencySummaryRequestDTO,
} from '../../../dto/urgencySummary/UrgencySummaryDTO'
import {
  getUrgencyHourly,
  getUrgencyLongestWaits,
  getUrgencyPriorities,
  getUrgencySummary,
  getUrgencyWhere,
} from '../../../services/UrgencySummaryController'
import { dayPoint } from '../../ServicesStatistics/periodDetail'
import { parseIsoDate } from '../../ServicesStatistics/statisticsUtils'
import { periodTitle } from '../../ServicesStatistics/TimelineChart'
import {
  openAttentionNoticeOf,
  templateDayPoint,
  templateReference,
  templatesOfDay,
  UrgencyDayReference,
  UrgencyMeasure,
} from '../urgencyAnalysis'
import useSectionData from '../useSectionData'
import UrgencyDayCauses from './UrgencyDayCauses'
import UrgencyDayKpis from './UrgencyDayKpis'
import UrgencyDayPriorities from './UrgencyDayPriorities'
import UrgencyDaySection from './UrgencyDaySection'
import UrgencyDayTemplates from './UrgencyDayTemplates'
import UrgencyHourlyChart from './UrgencyHourlyChart'
import UrgencyLongestWaits, { LONGEST_WAITS_FIRST, LONGEST_WAITS_MORE } from './UrgencyLongestWaits'

interface UrgencyDaySidebarProps {
  /** El día que se mira (el punto del gráfico); null con el panel cerrado. */
  day: UrgencyDailyPointDTO | null
  /** Los filtros de lo que se ve (las fechas son las del período de la pantalla: el panel pone las del día); null sin consulta. */
  request: UrgencySummaryRequestDTO | null
  measure: UrgencyMeasure
  /** El promedio de todo el período de la pantalla: contra él se compara el día. */
  reference: UrgencyDayReference
  /** Una fila por guardia con los números de todo el período de la pantalla: contra ellos se compara una guardia. */
  templates: UrgencyDimensionRowDTO[]
  /** Días con llegadas del período de la pantalla (para las llegadas de un día promedio de una guardia). */
  activeDays: number
  /** Con varias guardias y ninguna elegida, las filas dicen su guardia y se puede entrar a cada una. */
  showTemplate: boolean
  /** «Ver solo este día» (con una guardia, «Ver solo esta guardia y este día»): aplica el día, y la guardia si hay, a la pantalla. */
  onViewDay: (date: string, templateId: number | null) => void
  onHide: () => void
}

/** Una guardia abierta dentro del panel: su fila de «Por guardia» del día (llegadas y espera se ven al instante). */
interface Navigation {
  owner: UrgencyDailyPointDTO
  template: UrgencyDimensionRowDTO
}

const filterKey = (request: UrgencySummaryRequestDTO): string =>
  `${request.from}|${request.to}|${request.templateId}|${request.priorityId}|${request.financierId}`

/**
 * Detalle de un día del gráfico de evolución, en un panel lateral de Novarium (420 px, a todo el ancho en pantallas
 * angostas): un resumen en palabras, los indicadores contra el promedio del período, las atenciones con más espera, «Por
 * guardia», a qué hora llegaron, por código de triaje y las posibles causas por guardia. Cada sección hace su propia consulta
 * con el día como desde y hasta y con los filtros de la pantalla, y tiene su propia carga y su propio error con «Reintentar»;
 * las llegadas y la espera del título se ven al instante (vienen del gráfico). Los resultados quedan en un caché por día,
 * guardia y filtros, y lo que deja de verse se cancela.
 * Con varias guardias y ninguna elegida, tocar una fila de «Por guardia» abre, en el mismo panel, ese día solo con esa
 * guardia: el título es la guardia, el subtítulo es el día y un enlace «« {día}» vuelve; los indicadores se comparan contra el
 * promedio de esa guardia en el período. Esc cierra el panel entero y el foco vuelve a quien lo abrió (lo resuelve quien
 * abre el panel). Pie: «Ver solo este día» (o «Ver solo esta guardia y este día»).
 */
export default function UrgencyDaySidebar({
  day, request, measure, reference, templates, activeDays, showTemplate, onViewDay, onHide,
}: UrgencyDaySidebarProps) {
  const intl = useIntl()
  const titleRef = useRef<HTMLHeadingElement>(null)
  const lastKey = useRef('')
  const [navigation, setNavigation] = useState<Navigation | null>(null)
  /** Mientras el panel se cierra se sigue mostrando lo último (sin parpadeo de «Cargando»). */
  const lastShown = useRef<{ day: UrgencyDailyPointDTO; request: UrgencySummaryRequestDTO; template: UrgencyDimensionRowDTO | null } | null>(null)
  const template = day && navigation && navigation.owner === day ? navigation.template : null
  if (day && request) lastShown.current = { day, request, template }
  const shown = day && request ? { day, request, template } : lastShown.current
  const [expandedKey, setExpandedKey] = useState('')

  // Cerrado el panel, la guardia abierta se olvida: al volver a abrir el día se ve entero
  useEffect(() => {
    if (day === null) setNavigation(null)
  }, [day])

  const shownTemplate = shown?.template ?? null
  const dayRequest: UrgencySummaryRequestDTO | null = shown
    ? { ...shown.request, from: shown.day.date, to: shown.day.date, templateId: shownTemplate ? shownTemplate.id : shown.request.templateId }
    : null
  const baseKey = dayRequest ? filterKey(dayRequest) : null
  const expanded = baseKey !== null && expandedKey === baseKey
  const longestLimit = expanded ? LONGEST_WAITS_MORE : LONGEST_WAITS_FIRST
  const sectionKey = (name: string) => (baseKey === null ? null : `${baseKey}|${name}`)
  const required = (): UrgencySummaryRequestDTO => {
    if (!dayRequest) throw new Error('Panel sin día')
    return dayRequest
  }

  const summary = useSectionData(sectionKey('summary'), (signal) => getUrgencySummary(required(), signal))
  const hourly = useSectionData(sectionKey('hourly'), (signal) => getUrgencyHourly(required(), signal))
  const priorities = useSectionData(sectionKey('priorities'), (signal) => getUrgencyPriorities(required(), signal))
  const where = useSectionData(sectionKey('where'), (signal) => getUrgencyWhere(required(), signal))
  const longest = useSectionData(sectionKey(`longest|${longestLimit}`), (signal) => getUrgencyLongestWaits(required(), longestLimit, signal))

  // Al pasar de una vista a otra el foco va al título: el lector de pantalla lo lee y el teclado no se queda en el vacío
  const viewKey = day && request ? `${day.date}|${template ? template.id : 0}` : ''
  useEffect(() => {
    if (viewKey !== '' && lastKey.current !== '' && lastKey.current !== viewKey) titleRef.current?.focus()
    lastKey.current = viewKey
  }, [viewKey])

  if (!shown || !dayRequest) return null
  const dayTitle = periodTitle(intl, dayPoint(parseIsoDate(shown.day.date), shown.day.arrivals, shown.day.avgWaitMinutes, null), 'DAY')
  const isTemplate = shownTemplate !== null
  // Mirando una guardia, los números del día son los de esa guardia (la fila de «Por guardia» ya los traía)
  const shownDay = shownTemplate ? templateDayPoint(shown.day.date, shownTemplate) : shown.day
  const compareWith = shownTemplate
    ? templateReference(templates.find((row) => row.id === shownTemplate.id), reference, activeDays)
    : reference
  const longestRows = longest.data ?? (expanded ? longest.stale : null) ?? []
  const notice = summary.data && longest.data ? openAttentionNoticeOf(summary.data.current, longest.data) : null
  // Sin llegadas por código no hay nada que mostrar: la sección no aparece
  const noPriorities = priorities.status === 'ready' && (priorities.data ?? []).every((row) => row.arrivals === 0)
  // «Por guardia» sale de la consulta del día; con una sola guardia con llegadas no hay a dónde entrar
  const dayTemplates = showTemplate && !isTemplate ? templatesOfDay(summary.data?.byTemplate ?? []) : []
  const noTemplates = summary.status === 'ready' && dayTemplates.length < 2

  const openTemplate = (row: UrgencyDimensionRowDTO) => setNavigation({ owner: shown.day, template: row })
  const goBack = () => setNavigation(null)

  return (
    <Sidebar
      visible={day !== null}
      position="right"
      onHide={onHide}
      showCloseIcon={false}
      blockScroll
      className="nv-scope nv-drawer nv-urg-detail"
      aria-labelledby="nv-urg-detail-title"
    >
      <div className="nv-drawer-layout">
        <header className="nv-drawer-head">
          <div className="nv-stack-4">
            {isTemplate && (
              <Button
                type="button"
                link
                icon="pi pi-angle-double-left"
                label={dayTitle}
                aria-label={intl.formatMessage({ id: 'UrgencySummaryReturnTo' }, { period: dayTitle })}
                onClick={goBack}
              />
            )}
            <h2 className="nv-modal-title nv-urg-detail__title" id="nv-urg-detail-title" ref={titleRef} tabIndex={-1}>
              {shownTemplate ? shownTemplate.name : dayTitle}
            </h2>
            {isTemplate && <span className="nv-subtitle">{dayTitle}</span>}
          </div>
          <Button
            type="button"
            text
            className="nv-icon nv-tip"
            icon="pi pi-times"
            aria-label={intl.formatMessage({ id: 'UrgencySummaryDayClose' })}
            data-pr-tooltip={intl.formatMessage({ id: 'UrgencySummaryDayClose' })}
            onClick={onHide}
          />
        </header>

        <div className="nv-drawer-body">
          <UrgencyDayKpis
            day={shownDay}
            kpis={summary.data?.current ?? null}
            status={summary.status}
            onRetry={summary.retry}
            reference={compareWith}
            notice={notice}
          />

          {/* Lo que mejor explica un pico va justo después de los indicadores, como en estadísticas */}
          <UrgencyLongestWaits
            rows={longestRows}
            status={longest.status}
            expanded={expanded}
            day={shown.day.date}
            showTemplate={showTemplate && !isTemplate}
            onMore={() => setExpandedKey(baseKey ?? '')}
            onRetry={longest.retry}
          />

          {showTemplate && !isTemplate && !noTemplates && (
            <UrgencyDaySection
              id="nv-urg-detail-templates-title"
              title={intl.formatMessage({ id: 'UrgencySummaryDayTemplatesTitle' })}
              status={summary.status}
              failedMessageId="UrgencySummaryDayTemplatesFailed"
              onRetry={summary.retry}
            >
              <UrgencyDayTemplates rows={dayTemplates} dayTitle={dayTitle} onSelect={openTemplate} />
            </UrgencyDaySection>
          )}

          <UrgencyDaySection
            id="nv-urg-detail-hours-title"
            title={intl.formatMessage({ id: 'UrgencySummaryDayHoursTitle' })}
            status={hourly.status}
            failedMessageId="UrgencySummaryDayHoursFailed"
            onRetry={hourly.retry}
          >
            {hourly.data && (
              <UrgencyHourlyChart
                hours={hourly.data}
                measure={measure}
                height={240}
                ariaLabel={intl.formatMessage({ id: 'UrgencySummaryDayHoursAria' }, { day: shownTemplate ? `${shownTemplate.name}, ${dayTitle}` : dayTitle })}
              />
            )}
          </UrgencyDaySection>

          {!noPriorities && (
            <UrgencyDaySection
              id="nv-urg-detail-priorities-title"
              title={intl.formatMessage({ id: 'UrgencySummaryDayPrioritiesTitle' })}
              status={priorities.status}
              failedMessageId="UrgencySummaryDayPrioritiesFailed"
              onRetry={priorities.retry}
            >
              {priorities.data && <UrgencyDayPriorities rows={priorities.data} />}
            </UrgencyDaySection>
          )}

          <UrgencyDaySection
            id="nv-urg-detail-causes-title"
            title={intl.formatMessage({ id: 'UrgencySummaryDayCausesTitle' })}
            status={where.status}
            failedMessageId="UrgencySummaryDayCausesFailed"
            onRetry={where.retry}
          >
            {where.data && <UrgencyDayCauses rows={where.data} single={!showTemplate || isTemplate} />}
          </UrgencyDaySection>
        </div>

        <footer className="nv-drawer-foot">
          <Button
            type="button"
            className="buttonMain3"
            label={intl.formatMessage({ id: isTemplate ? 'UrgencySummaryDayViewOnlyTemplate' : 'UrgencySummaryDayViewOnly' })}
            onClick={() => onViewDay(shown.day.date, shownTemplate ? shownTemplate.id : null)}
          />
        </footer>
      </div>
    </Sidebar>
  )
}
